import React, { useEffect, useState } from 'react';
import { useFramework } from '@/framework';
import { CoreAPI, useIdentity } from '../../core';
import { StageAPI, useIsLongImageMode, useScale } from '../../stage';

const useRefreshStage = (originIndex: number) => {
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);
    const [_, refresh] = useState(0);
    useEffect(() => {
        const handler = coreApi.events.onRerenderStage(originIndex, () => {
            refresh((prev) => prev + 1);
        });
        return () => coreApi.events.off(handler);
    }, [originIndex]);
};

const SingleStage: React.FC<{ children?: React.ReactNode; originIndexOrLayerId: string | number }> = ({
    children,
    originIndexOrLayerId,
}) => {
    const identity = useIdentity();
    const scale = useScale();
    const { width, height, originIndex } = CoreAPI(identity).tryGetStageTransform(originIndexOrLayerId, scale, false);
    useRefreshStage(originIndex);
    return (
        <div className="editor-plugin__stage" style={{ minWidth: width, height: height }}>
            {children}
        </div>
    );
};

const VarietyStage: React.FC<{
    children?: React.ReactNode;
    originIndexOrLayerId: string | number;
    enableMouseEvents?: boolean;
    isWrapOnly?: boolean;
    name?: string;
}> = ({ children, originIndexOrLayerId, enableMouseEvents, isWrapOnly, name }) => {
    const identity = useIdentity();
    const scale = useScale();
    const size = StageAPI(identity).getSize();
    const framework = useFramework();
    const { offsetTop, width, height, originIndex } = CoreAPI(identity).tryGetStageTransform(
        originIndexOrLayerId,
        scale,
        true,
    );
    // if (name === 'stage-operate') {
    //     console.log('scaleWidth VarietyStage -> ', name, originIndex, originIndexOrLayerId, offsetTop);
    // }
    useRefreshStage(originIndex);
    const mouseEvents = enableMouseEvents
        ? {
              onMouseEnter: () => {
                  framework.events.emitMouseEnter(originIndex, true);
              },
              onMouseLeave: () => {
                  framework.events.emitMouseLeave(originIndex, true);
              },
          }
        : {};

    return (
        <div className="editor-plugin__stage" style={{ minWidth: size.width * scale, height: size.height * scale }}>
            {isWrapOnly ? (
                <> {children}</>
            ) : (
                <div
                    style={{
                        width,
                        height,
                        position: 'absolute',
                        top: offsetTop,
                    }}
                    {...mouseEvents}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export const BaseStagePlugin = React.forwardRef<
    HTMLDivElement,
    {
        name?: string;
        originIndexOrLayerId: string | number;
        children: React.ReactNode;
        /** 是否启用事件，默认 false */
        enablePointerEvents?: boolean;
        /** 长图模式是否启用鼠标hover事件，默认 false */
        enableMouseEvents?: boolean;
        /** 长图模式是否只显示外壳，默认 false */
        isWrapOnly?: boolean;
        /** 自定义样式 */
        style?: React.CSSProperties;
    }
>(({ name, originIndexOrLayerId, children, enablePointerEvents, enableMouseEvents, isWrapOnly, style }, ref) => {
    const isLongImageMode = useIsLongImageMode();
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);
    const [_, refresh] = useState(0);

    useEffect(() => {
        const resize = coreApi.events.onStageResize(() => refresh((prev) => prev + 1));
        const rerender = coreApi.events.onRerenderAll(() => refresh((prev) => prev + 1));
        return () => {
            coreApi.events.off(resize);
            coreApi.events.off(rerender);
        };
    }, []);

    const stylePointerEvents: React.CSSProperties = enablePointerEvents ? { pointerEvents: 'initial' } : {};

    return (
        <div ref={ref} className="editor-plugin" data-plugin={name} style={{ ...style, ...stylePointerEvents }}>
            {isLongImageMode ? (
                <VarietyStage
                    name={name}
                    isWrapOnly={isWrapOnly}
                    enableMouseEvents={enableMouseEvents}
                    originIndexOrLayerId={originIndexOrLayerId}
                >
                    {children}
                </VarietyStage>
            ) : (
                <SingleStage originIndexOrLayerId={originIndexOrLayerId}>{children}</SingleStage>
            )}
        </div>
    );
});

BaseStagePlugin.displayName = 'BaseStagePlugin';
